import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface RegistrationSuccessData {
  firstName: string;
  lastName: string;
  email: string;
  tempPassword: string;
  message?: string;
}

@Component({
  selector: 'app-patient-registration-success-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <div class="success-dialog">
      <!-- Header -->
      <div class="dialog-header">
        <button 
          mat-icon-button 
          class="close-button" 
          (click)="close()"
          matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
        <mat-icon class="success-icon">check_circle</mat-icon>
        <h2 mat-dialog-title>Patient Registered Successfully!</h2>
      </div>

      <!-- Content -->
      <mat-dialog-content class="dialog-content">
        <div class="patient-info">
          <p class="info-text">
            <strong>{{ data.firstName }} {{ data.lastName }}</strong> has been successfully registered.
          </p>
          <p class="warning-text">
            <mat-icon class="warning-icon">info</mat-icon>
            Please share the following credentials with the patient securely.
          </p>
        </div>

        <!-- Credentials Card -->
        <div class="credentials-card">
          <div class="credential-row">
            <div class="credential-label">
              <mat-icon>email</mat-icon>
              <span>Email</span>
            </div>
            <div class="credential-value">
              <span>{{ data.email }}</span>
              <button 
                mat-icon-button 
                (click)="copyToClipboard(data.email, 'Email')"
                matTooltip="Copy email">
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>
          </div>

          <div class="credential-row">
            <div class="credential-label">
              <mat-icon>lock</mat-icon>
              <span>Temporary Password</span>
            </div>
            <div class="credential-value">
              <span class="password-text">{{ data.tempPassword }}</span>
              <button 
                mat-icon-button 
                (click)="copyToClipboard(data.tempPassword, 'Password')"
                matTooltip="Copy password">
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Security Notice -->
        <div class="security-notice">
          <mat-icon>security</mat-icon>
          <div>
            <p><strong>Important Security Notice:</strong></p>
            <ul>
              <li>The patient must change this password on first login</li>
              <li>Share these credentials through a secure channel</li>
              <li>This information will not be shown again</li>
            </ul>
          </div>
        </div>
      </mat-dialog-content>

      <!-- Actions -->
      <mat-dialog-actions class="dialog-actions">
        <button 
          mat-stroked-button 
          (click)="copyAllCredentials()"
          class="copy-all-btn">
          <mat-icon>content_copy</mat-icon>
          Copy All Credentials
        </button>
        <button 
          mat-raised-button 
          color="primary"
          (click)="close()"
          class="close-btn">
          I've Saved The Credentials
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .success-dialog {
      padding: 0;
      max-width: 600px;
    }

    .dialog-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 24px;
      text-align: center;
      border-radius: 4px 4px 0 0;
      position: relative;
    }

    .close-button {
      position: absolute;
      top: 8px;
      right: 8px;
      color: white;
      opacity: 0.9;
      transition: opacity 0.2s;
    }

    .close-button:hover {
      opacity: 1;
      background-color: rgba(255, 255, 255, 0.1);
    }

    .close-button mat-icon {
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .success-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      margin-bottom: 8px;
    }

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 500;
    }

    .dialog-content {
      padding: 24px !important;
    }

    .patient-info {
      margin-bottom: 24px;
    }

    .info-text {
      font-size: 16px;
      margin-bottom: 16px;
      color: #333;
    }

    .warning-text {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      background-color: #fff3cd;
      border-left: 4px solid #ffc107;
      border-radius: 4px;
      color: #856404;
      font-size: 14px;
    }

    .warning-icon {
      color: #ffc107;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .credentials-card {
      background-color: #f8f9fa;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 24px;
    }

    .credential-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #dee2e6;
    }

    .credential-row:last-child {
      border-bottom: none;
    }

    .credential-label {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #6c757d;
      font-size: 14px;
      font-weight: 500;
    }

    .credential-label mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .credential-value {
      display: flex;
      align-items: center;
      gap: 8px;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: #212529;
      background-color: white;
      padding: 8px 12px;
      border-radius: 4px;
      border: 1px solid #dee2e6;
    }

    .password-text {
      font-weight: 600;
      color: #667eea;
    }

    .security-notice {
      display: flex;
      gap: 12px;
      padding: 16px;
      background-color: #e7f3ff;
      border-left: 4px solid #2196f3;
      border-radius: 4px;
      font-size: 13px;
      color: #014361;
    }

    .security-notice mat-icon {
      color: #2196f3;
      font-size: 24px;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

    .security-notice ul {
      margin: 8px 0 0 0;
      padding-left: 20px;
    }

    .security-notice li {
      margin-bottom: 4px;
    }

    .dialog-actions {
      padding: 16px 24px !important;
      background-color: #f8f9fa;
      border-top: 1px solid #dee2e6;
      justify-content: space-between;
    }

    .copy-all-btn {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .close-btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    }

    button mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
    }

    @media (max-width: 600px) {
      .credential-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      .credential-value {
        width: 100%;
      }

      .dialog-actions {
        flex-direction: column;
        gap: 8px;
      }

      .copy-all-btn,
      .close-btn {
        width: 100%;
      }
    }
  `]
})
export class PatientRegistrationSuccessDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RegistrationSuccessData,
    private dialogRef: MatDialogRef<PatientRegistrationSuccessDialogComponent>,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar
  ) { }

  copyToClipboard(text: string, label: string): void {
    const success = this.clipboard.copy(text);
    if (success) {
      this.snackBar.open(`${label} copied to clipboard!`, 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    }
  }

  copyAllCredentials(): void {
    const credentialsText = `
Patient Registration Credentials
================================

Name: ${this.data.firstName} ${this.data.lastName}
Email: ${this.data.email}
Temporary Password: ${this.data.tempPassword}

IMPORTANT: Patient must change password on first login.
Share these credentials securely.
    `.trim();

    const success = this.clipboard.copy(credentialsText);
    if (success) {
      this.snackBar.open('All credentials copied to clipboard!', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}